import { useCallback, useRef, useEffect, useState, useMemo } from 'react';
import type { QuoteDTO } from '@setvi/shared/services/react-query';
import {
  updateItemOptimistically,
  extractItems,
  buildQuoteRequestBody
} from '../utils/quote-helpers';
import { useQuoteCache } from './use-quote-cache';
import { useQueuedUpdateMutation } from './use-queued-update-mutation';

interface ItemUpdates {
  quantity?: number;
  sell?: number;
  note?: string;
  labelType?: string;
  customLabel?: string;
  includeInTotalCost?: boolean;
}

interface UpdateItemParams {
  itemId: string;
  updates: ItemUpdates;
}

/**
 * Debounced item update hook that accumulates changes
 *
 * Key behavior:
 * 1. Applies optimistic updates IMMEDIATELY to cache (instant UI feedback)
 * 2. Debounces only the API save operation
 * 3. Captures pre-change state before first edit for proper rollback
 * 4. All accumulated changes are saved when debounce fires
 *
 * This solves the issue where rapid edits to multiple items would lose changes.
 */
export const useDebouncedItemUpdate = (quoteId: string, delay = 1500) => {
  const cache = useQuoteCache(quoteId);
  const {
    mutateAsync: updateQuote,
    hasQueuedMutations,
    setOnQueueEmpty
  } = useQueuedUpdateMutation();

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousQuoteRef = useRef<QuoteDTO | undefined>(undefined);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  const mutate = useCallback(
    ({ itemId, updates }: UpdateItemParams) => {
      // Capture pre-change state on FIRST change only (before any optimistic updates)
      // This ensures we can rollback ALL accumulated changes on error
      if (!timeoutRef.current) {
        previousQuoteRef.current = cache.getQuote();
      }

      // Apply optimistic update IMMEDIATELY for instant UI feedback
      cache.setQuote(old => updateItemOptimistically(old, itemId, updates));

      // Cancel existing debounce timer
      cancel();

      // Set new debounce timer for API call
      timeoutRef.current = setTimeout(async () => {
        timeoutRef.current = null;
        setIsLoading(true);
        setError(null);

        try {
          await cache.cancel();

          const currentQuote = cache.getQuote();
          if (!currentQuote) {
            throw new Error('Quote not found in cache');
          }

          const items = extractItems(currentQuote.items);

          await updateQuote({
            quoteId,
            body: buildQuoteRequestBody(currentQuote, items)
          });

          // Skip invalidate if there are queued mutations to preserve optimistic updates
          if (hasQueuedMutations()) {
            // Defer invalidate until all queued mutations complete
            setOnQueueEmpty(() => cache.invalidate());
          } else {
            // No queued mutations, invalidate immediately
            cache.invalidate();
          }
        } catch (err) {
          // Rollback ALL accumulated changes on error
          if (previousQuoteRef.current) {
            cache.setQuote(() => previousQuoteRef.current);
          }
          setError(err instanceof Error ? err : new Error('Update failed'));
        } finally {
          setIsLoading(false);
          previousQuoteRef.current = undefined;
        }
      }, delay);
    },
    [
      cache,
      cancel,
      delay,
      quoteId,
      updateQuote,
      hasQueuedMutations,
      setOnQueueEmpty
    ]
  );

  return useMemo(
    () => ({
      mutate,
      cancel,
      isLoading,
      error
    }),
    [mutate, cancel, isLoading, error]
  );
};
